import React from 'react';
import { NavLink } from 'react-router-dom';
import { theme } from '../design/theme';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

interface SidebarProps {
  items: { label: string; path: string; icon?: React.ReactNode }[];
  title: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ items, title }) => {
  const { logout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const sidebarWidth = isCollapsed ? theme.sidebar.collapsedWidth : theme.sidebar.width;

  React.useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
  }, [sidebarWidth]);

  return (
    <aside style={{
      width: sidebarWidth,
      backgroundColor: theme.sidebar.background,
      color: theme.sidebar.text,
      height: '100vh',
      position: 'fixed',
      left: 0,
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 100,
      overflow: 'hidden',
      boxShadow: '4px 0 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        padding: `0 ${theme.spacing.lg}`,
        borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '72px',
        whiteSpace: 'nowrap',
        flexShrink: 0
      }}>
        {!isCollapsed && (
          <span style={{ 
            ...theme.typography.bodyBold, 
            fontSize: '18px', 
            color: '#FFFFFF',
            letterSpacing: '-0.5px'
          }}>
            {title}
          </span>
        )}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            background: theme.colors.neutral[800],
            border: 'none',
            color: '#FFFFFF',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.neutral[700]}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.neutral[800]}
        >
          <span style={{ fontSize: '12px' }}>{isCollapsed ? '→' : '←'}</span>
        </button>
      </div>

      <nav style={{ flex: 1, padding: theme.spacing.md, overflowY: 'auto', overflowX: 'hidden' }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '4px' }}>
              <NavLink 
                to={item.path}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  borderRadius: '8px',
                  textDecoration: 'none',
                  color: isActive ? theme.sidebar.activeText : theme.colors.neutral[400],
                  backgroundColor: isActive ? theme.sidebar.activeBackground : 'transparent',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  fontWeight: isActive ? '600' : '500',
                  fontSize: '14px'
                })}
                onMouseEnter={(e) => {
                  if (!(e.currentTarget as HTMLElement).classList.contains('active')) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = theme.sidebar.hoverBackground;
                    (e.currentTarget as HTMLElement).style.color = '#FFFFFF';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(e.currentTarget as HTMLElement).classList.contains('active')) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = theme.colors.neutral[400];
                  }
                }}
              >
                <span style={{ 
                  marginRight: isCollapsed ? 0 : theme.spacing.md,
                  fontSize: '18px',
                  width: '20px',
                  textAlign: 'center'
                }}>
                  {item.icon || '•'}
                </span>
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div style={{
        padding: theme.spacing.md,
        borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
        backgroundColor: 'transparent',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
        <button 
          onClick={toggleTheme}
          style={{
            width: '100%',
            padding: theme.spacing.md,
            backgroundColor: 'transparent',
            border: 'none',
            color: theme.colors.neutral[400],
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: theme.spacing.md,
            transition: 'all 0.2s ease',
            ...theme.typography.caption,
            fontWeight: '600',
            fontSize: '14px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.color = '#FFFFFF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = theme.colors.neutral[400];
          }}
        >
          <span style={{ fontSize: '18px' }}>{mode === 'light' ? '🌙' : '☀️'}</span>
          {!isCollapsed && <span>{mode === 'light' ? 'Dark Mode' : 'Light Mode'}</span>}
        </button>

        <button 
          onClick={logout}
          style={{
            width: '100%',
            padding: theme.spacing.md,
            backgroundColor: 'transparent',
            border: 'none',
            color: theme.colors.neutral[400],
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: theme.spacing.md,
            transition: 'all 0.2s ease',
            ...theme.typography.caption,
            fontWeight: '600',
            fontSize: '14px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.color = '#EF4444';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = theme.colors.neutral[400];
          }}
        >
          <span style={{ fontSize: '18px' }}>⏻</span>
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};
